import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Question, CustomerAdmin } from '../../models/communication.model';
import { CommunicationService } from '../../services/communication.service';

@Component({
  selector: 'app-communication-panel',
  templateUrl: './communication-panel.component.html',
  styleUrls: ['./communication-panel.component.scss']
})
export class CommunicationPanelComponent implements OnInit, OnDestroy {

  //The main changes
  questions: Question[] = [];
  selectedQuestion: Question | null = null;
  isLoadingQuestions = false;

  showAnswerForm = false;
  showClarifyForm = false;
  answerText = '';
  clarificationText = '';
  selectedCustomerAdminId: string | null = null;
  customerAdmins: CustomerAdmin[] = [];
  searchText: string = '';
  private searchSubject = new Subject<string>();
  noResultsFound: boolean = false;

  private questionsSubscription: Subscription | undefined;
  private customerAdminsSubscription: Subscription | undefined;

  selectedCustomerAdminChat: CustomerAdmin | null = null;

  attachedImageFile: File | null = null;
  attachedImagePreview: string | null = null;

  constructor(private communicationService: CommunicationService) {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      this.searchText = value;
    });
  }

  ngOnInit(): void {
    this.loadQuestions();
    this.loadCustomerAdmins();
  }

  ngOnDestroy(): void {
    this.questionsSubscription?.unsubscribe();
    this.customerAdminsSubscription?.unsubscribe();
  }



  //Receive questions and administrators from the service (communicationService) via HTTP requests.
  loadQuestions(): void {
    this.isLoadingQuestions = true;
    this.questionsSubscription = this.communicationService.getQuestions().subscribe(
      (data) => {
        // Store all questions including clarifications for internal use
        this.questions = data;

        // If we have a selected question, refresh it
        if (this.selectedQuestion) {
          const refreshedSelectedQuestion = this.questions.find(q => q.id === this.selectedQuestion?.id);
          if (refreshedSelectedQuestion) {
            this.selectedQuestion = refreshedSelectedQuestion;
          } else {
            this.closeDetailView();
          }
        }

        this.isLoadingQuestions = false;
      },
      (error) => {
        console.error('Error loading questions:', error);
        this.isLoadingQuestions = false;
      }
    );
  }

  //Receive customer administrators from the service (communicationService) via HTTP requests.
  loadCustomerAdmins(): void {
    this.customerAdminsSubscription = this.communicationService.getCustomerAdmins().subscribe(
      (data) => {
        this.customerAdmins = data;
      },
      (error) => {
        console.error('Error loading customer admins:', error);
      }
    );
  }

  //Select a question and close the customer-admin chat if it is open.
  selectQuestion(question: Question): void {
    this.selectedQuestion = question;
    this.cancelReply();
    // Close the customer-admin chat if it is open
    this.selectedCustomerAdminChat = null;
  }

  //Answer a question.
  answerQuestion(question: Question): void {
    if (this.showClarifyForm) {
      this.showClarifyForm = false;
    }
    this.showAnswerForm = !this.showAnswerForm;
    this.answerText = '';
  }

  //Send an answer to a question.
  sendAnswer(question: Question): void {
    if (!this.answerText.trim() && !this.attachedImageFile) {
      console.warn('Answer text and image are empty');
      return;
    }

    const formData = new FormData();
    formData.append('text', this.answerText.trim());

    if (this.attachedImageFile) {
      formData.append('image', this.attachedImageFile);
    }

    this.communicationService.addAnswerToQuestion(question.id, formData).subscribe({
      next: (updatedQuestion) => {
        if (this.selectedQuestion && this.selectedQuestion.id === updatedQuestion.id) {
          this.selectedQuestion = updatedQuestion;
        }
        this.showAnswerForm = false;
        this.answerText = '';
        this.attachedImageFile = null;
        this.attachedImagePreview = null;
      },
      error: (err) => console.error('Error sending answer:', err)
    });
  }

  sendClarification(originalQuestion: Question): void {
    if (!this.selectedCustomerAdminChat) {
      console.error('No customer admin selected');
      return;
    }
    if (!this.clarificationText.trim() && !this.attachedImageFile) {
      console.warn('Clarification text and image are empty');
      return;
    }
    const formData = new FormData();
    formData.append('text', this.clarificationText.trim());
    if (this.attachedImageFile) {
      formData.append('image', this.attachedImageFile);
    }
    this.communicationService.sendMessageToCustomerAdmin(this.selectedCustomerAdminChat.id, formData, originalQuestion.id).subscribe({
      next: () => {
        // Set status to pending_customer if not already
        if (originalQuestion.status !== 'pending_customer') {
          this.onStatusChange(originalQuestion, 'pending_customer');
        }
        this.clarificationText = '';
        this.attachedImageFile = null;
        this.attachedImagePreview = null;
      },
      error: (error: Error) => console.error('Error sending clarification:', error)
    });
  }

  cancelReply(): void {
    this.showAnswerForm = false;
    this.showClarifyForm = false;
    this.answerText = '';
    this.clarificationText = '';
    this.selectedCustomerAdminId = null;
    // Закриваємо чат з customer-admin
    this.selectedCustomerAdminChat = null;
  }

  closeDetailView(): void {
    this.selectedQuestion = null;
    this.cancelReply();
  }

  getStatusClass(status: Question['status']): string {
    switch (status) {
      case 'new': return 'status-new';
      case 'answered': return 'status-answered';
      case 'pending_customer': return 'status-pending';
      default: return '';
    }
  }

  getChatThread(question: Question): Question[] {
    if (!question) return [];
    const threadRoot = question.relatedToOriginalQuestionId
      ? this.questions.find(q => q.id === question.relatedToOriginalQuestionId)
      : question;
    if (!threadRoot) return [];

    // Only include messages that are NOT customer admin clarifications
    // and are either the root question or direct responses to it
    const threadMessages = this.questions.filter(q =>
      (q.id === threadRoot.id || q.relatedToOriginalQuestionId === threadRoot.id) &&
      !q.targetCustomerAdmin && // Exclude customer admin clarifications
      q.authorRole !== 'customer-admin' // Exclude customer admin messages
    );
    return threadMessages;
  }

  onStatusChange(question: Question, newStatus: Question['status']): void {
    this.communicationService.updateQuestionStatus(question.id, newStatus).subscribe({
      next: (updatedQuestion) => {
        if (this.selectedQuestion && this.selectedQuestion.id === updatedQuestion.id) {
          this.selectedQuestion = updatedQuestion;
        }
      },
      error: (err) => console.error(`Error updating status for question ${question.id}:`, err)
    });
  }

  selectedStatus: string = 'all';
  sortOrder: 'newest' | 'oldest' = 'newest';

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedStatus = 'all';
    this.sortOrder = 'newest';
  }

  get filteredQuestions(): Question[] {
    // First filter out any internal clarifications
    let filtered = this.questions.filter(q => !q.targetCustomerAdmin);
    const searchTerm = this.searchText.toLowerCase().trim();

    if (searchTerm) {
      filtered = filtered.filter(q =>
        q.text.toLowerCase().includes(searchTerm) ||
        q.id.toLowerCase().includes(searchTerm) ||
        q.authorName.toLowerCase().includes(searchTerm)
      );
    }

    if (this.selectedStatus !== 'all') {
      filtered = filtered.filter(q => q.status === this.selectedStatus);
    }

    filtered = filtered.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime();
      const dateB = new Date(b.updatedAt).getTime();
      return this.sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    this.noResultsFound = filtered.length === 0;
    return filtered;
  }

  onImageSelected(event: Event): void {
    const fileInput = event.target as HTMLInputElement;
    if (fileInput.files && fileInput.files.length > 0) {
      this.attachedImageFile = fileInput.files[0];

      const reader = new FileReader();
      reader.onload = () => {
        this.attachedImagePreview = reader.result as string;
      };
      reader.readAsDataURL(this.attachedImageFile);
    }
  }

  removeAttachedImage(): void {
    this.attachedImageFile = null;
    this.attachedImagePreview = null;
  }

  onCustomerAdminSelect(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const adminId = select.value;
    this.selectedCustomerAdminId = adminId;
    this.selectedCustomerAdminChat = this.customerAdmins.find(admin => admin.id === adminId) || null;

    if (this.selectedCustomerAdminChat && this.selectedQuestion) {
      this.clarificationText = `Original question from ${this.selectedQuestion.authorName} (${this.selectedQuestion.authorRole}):\n"${this.selectedQuestion.text}"\n\nAdmin seeking clarification:`;
    }
  }


  getCustomerAdminChatMessages(): Question[] {
    if (!this.selectedCustomerAdminChat || !this.selectedQuestion) return [];
    return this.questions.filter(q =>
      q.targetCustomerAdmin?.id === this.selectedCustomerAdminChat!.id &&
      q.relatedToOriginalQuestionId === this.selectedQuestion!.id
    ).sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
  }

  // Returns all customer-admin messages for this issue
  getCustomerAdminChatsForIssue(issue: Question): Question[] {
    return this.questions.filter(q =>
      q.relatedToOriginalQuestionId === issue.id && q.targetCustomerAdmin
    );
  }

  // Returns unique customer-admins involved in this issue (even if deleted)
  getCustomerAdminsForIssue(issue: Question): CustomerAdmin[] {
    const messages = this.getCustomerAdminChatsForIssue(issue);
    const uniqueAdmins: { [id: string]: CustomerAdmin } = {};
    messages.forEach(msg => {
      if (msg.targetCustomerAdmin) {
        uniqueAdmins[msg.targetCustomerAdmin.id] = msg.targetCustomerAdmin;
      }
    });
    return Object.values(uniqueAdmins);
  }

  // Returns true if the customer admin is still active (present in customerAdmins)
  isCustomerAdminActive(ca: CustomerAdmin): boolean {
    return this.customerAdmins.some(admin => admin.id === ca.id);
  }

  // Returns all messages for this issue and this customer-admin
  getCustomerAdminChatMessagesForIssue(issue: Question, ca: CustomerAdmin): Question[] {
    return this.questions.filter(q =>
      q.relatedToOriginalQuestionId === issue.id &&
      q.targetCustomerAdmin?.id === ca.id
    ).sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
  }

  // Sends a clarification message to the given customer-admin for this issue
  sendClarificationToCustomerAdmin(issue: Question, ca: CustomerAdmin): void {
    if (!this.clarificationText.trim() && !this.attachedImageFile) {
      console.warn('Clarification text and image are empty');
      return;
    }
    const formData = new FormData();
    formData.append('text', this.clarificationText.trim());
    if (this.attachedImageFile) {
      formData.append('image', this.attachedImageFile);
    }
    this.communicationService.sendMessageToCustomerAdmin(ca.id, formData, issue.id).subscribe({
      next: () => {
        // Set status to pending_customer if not already
        if (issue.status !== 'pending_customer') {
          this.onStatusChange(issue, 'pending_customer');
        }
        this.clarificationText = '';
        this.attachedImageFile = null;
        this.attachedImagePreview = null;
      },
      error: (error: Error) => console.error('Error sending clarification:', error)
    });
  }
}
